/**
 * Grouping bars (EXPERIMENTAL, plan §"Grouping bars") — derives vertical
 * "belongs together" bars per therapist column from the event index:
 *
 *  1. ADDRESS groups: runs of appointments at the same physical address —
 *     institutions (Heim) are labeled with the facility business name;
 *     private shared addresses (e.g. family members) get the generic
 *     label "im selben Haus".
 *  2. PATIENT groups: back-to-back appointments of one patient, labeled
 *     with the patient's last name.
 *
 * IMPORTANT — address identity is CONTENT-based, never id-based: the same
 * Heim exists as one address row per patient (verified in data: one street
 * with 47 address rows, 9 businessName spellings, "…str." vs "…straße").
 * We normalize street + zip into a key that survives those variants.
 *
 * Encapsulation contract (rollback = delete this file + the overlay + flag):
 * pure functions over MCEventIndex/MCResource, reading only MCEvent fields
 * and `raw.extendedProps`. No imports from grid internals besides types.
 */
import type { ClientAddress } from "../../Calendar/controller/adapters/types";
import { MCEvent, MCResource } from "./types";
import { MCEventIndex, eventsForResource } from "./eventIndex";

export type MCGroupingBarKind = "address" | "patient";

export interface MCGroupingBar {
  id: string;
  resourceId: string;
  kind: MCGroupingBarKind;
  /** Rotated label: facility business name / patient last name */
  label: string;
  /** Tooltip: full address / patient + count */
  title: string;
  /** Uniform sublocality color of the members, when they agree */
  color?: string;
  startMin: number;
  endMin: number;
  /** Members in start order — feeds programmatic selection & bar-drag */
  eventIds: ReadonlyArray<string>;
  memberCount: number;
  /** Horizontal offset lane when bars overlap in time (0 = column edge) */
  lane: number;
}

export interface GroupingBarOptions {
  /** Max gap (min) bridged inside one address run (lunch at the Heim etc.) */
  addressGapMin: number;
  /** Max gap (min) between back-to-back appointments of one patient */
  patientGapMin: number;
  /** Minimum members before a bar is worth drawing */
  minMembers: number;
}

export const DEFAULT_GROUPING_OPTIONS: GroupingBarOptions = {
  addressGapMin: 45,
  patientGapMin: 5,
  minMembers: 2,
};

/** Feature flag — default ON; kill switch via localStorage "0". */
export const GROUPING_BARS_FLAG = "mc.groupingBars";
export const isGroupingBarsEnabled = (): boolean => {
  try {
    return window.localStorage.getItem(GROUPING_BARS_FLAG) !== "0";
  } catch {
    return true;
  }
};

// ---------------------------------------------------------------------------
// Address identity (content-based)
// ---------------------------------------------------------------------------

/**
 * Normalize a street line so real-world spelling variants of the SAME
 * physical address collide: case, whitespace, "straße/strasse/str." endings,
 * punctuation. ("Neue Knochenhauerstr. 20" ≡ "neue knochenhauerstraße 20")
 */
export const normalizeStreet = (street: string): string =>
  street
    .toLowerCase()
    .replace(/stra(ß|ss)e\b/g, "str")
    .replace(/str\./g, "str")
    .replace(/[.,\-/]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const addressOf = (event: MCEvent): ClientAddress | undefined =>
  event.raw.extendedProps?.address as ClientAddress | undefined;

/** Content key for "same physical address", or null when unusable. */
export const addressKeyOf = (event: MCEvent): string | null => {
  const address = addressOf(event);
  const street = address?.street?.trim();
  if (!street) return null;
  return `${normalizeStreet(street)}|${(address?.postalCode ?? "").trim()}`;
};

// ---------------------------------------------------------------------------
// Run building
// ---------------------------------------------------------------------------

interface Run {
  key: string;
  events: MCEvent[];
  endMin: number;
}

/**
 * Same-key contiguity runs. Events are grouped by key FIRST, then chained by
 * gap — an interleaved event of another address/patient (parallel lane) must
 * not split a run it doesn't belong to.
 */
const buildRuns = (
  events: ReadonlyArray<MCEvent>,
  keyOf: (event: MCEvent) => string | null,
  gapMin: number,
): Run[] => {
  const byKey = new Map<string, MCEvent[]>();
  for (const event of events) {
    const key = keyOf(event);
    if (!key) continue;
    let list = byKey.get(key);
    if (!list) byKey.set(key, (list = []));
    list.push(event); // input is start-sorted, so lists stay sorted
  }
  const runs: Run[] = [];
  for (const [key, list] of byKey) {
    let current: Run | null = null;
    for (const event of list) {
      if (current && event.startMin <= current.endMin + gapMin) {
        current.events.push(event);
        current.endMin = Math.max(current.endMin, event.endMin);
      } else {
        current = { key, events: [event], endMin: event.endMin };
        runs.push(current);
      }
    }
  }
  return runs;
};

/** Most frequent non-empty value (ties: first seen) — for label voting. */
const majority = (values: Array<string | undefined>): string | undefined => {
  const counts = new Map<string, number>();
  let best: string | undefined;
  let bestCount = 0;
  for (const value of values) {
    const v = value?.trim();
    if (!v) continue;
    const count = (counts.get(v) ?? 0) + 1;
    counts.set(v, count);
    if (count > bestCount) {
      best = v;
      bestCount = count;
    }
  }
  return best;
};

const uniformColor = (events: MCEvent[]): string | undefined => {
  const first = events[0]?.sublocalityColor;
  if (!first) return undefined;
  return events.every((e) => e.sublocalityColor === first) ? first : undefined;
};

const patientLastName = (event: MCEvent): string | undefined => {
  const patient = event.raw.extendedProps?.patient as { lastName?: string } | undefined;
  return patient?.lastName || event.patientLabel;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Derive grouping bars for every therapist column.
 *
 * Precedence: address runs with ≥2 DISTINCT patients win (a Heim block);
 * their members are consumed. Remaining events form patient runs (same
 * patient back-to-back). Runs below `minMembers` are dropped.
 */
export const buildGroupingBars = (
  index: MCEventIndex,
  resources: ReadonlyArray<MCResource>,
  options: Partial<GroupingBarOptions> = {},
): MCGroupingBar[] => {
  const opts = { ...DEFAULT_GROUPING_OPTIONS, ...options };
  const bars: MCGroupingBar[] = [];

  for (const resource of resources) {
    const columnBars: MCGroupingBar[] = [];

    // Only patient treatment slots participate; breaks/Weg/groups/etc.
    // neither join nor split a run (gap thresholds decide).
    const appointments = eventsForResource(index, resource.id).filter(
      (e) => e.kind === "appointment",
    );
    if (appointments.length < opts.minMembers) continue;

    const consumed = new Set<string>();

    // 1) Address groups (multi-patient runs at one physical address).
    for (const run of buildRuns(appointments, addressKeyOf, opts.addressGapMin)) {
      if (run.events.length < opts.minMembers) continue;
      const patients = new Set(run.events.map((e) => e.patientId ?? e.patientLabel));
      if (patients.size < 2) continue; // single patient → patient bar territory

      const address = addressOf(run.events[0]);
      // Institutions (Heim etc.) carry a business name. A private home
      // address shared by several patients (typically family) does not —
      // label those generically instead of exposing sublocality/street.
      const businessName = majority(
        run.events.map((e) => addressOf(e)?.businessName),
      );
      const label = businessName ?? "im selben Haus";
      run.events.forEach((e) => consumed.add(e.id));
      columnBars.push({
        id: `addr:${resource.id}:${run.events[0].startMin}`,
        resourceId: resource.id,
        kind: "address",
        label,
        title: `${label} — ${address?.fullAddress ?? ""} (${run.events.length} Termine, ${patients.size} Patienten)`,
        color: uniformColor(run.events),
        startMin: run.events[0].startMin,
        endMin: run.endMin,
        eventIds: run.events.map((e) => e.id),
        memberCount: run.events.length,
        lane: 0,
      });
    }

    // 2) Patient groups (same patient back-to-back) on what's left.
    const rest = appointments.filter((e) => !consumed.has(e.id));
    const patientKey = (e: MCEvent): string | null =>
      e.patientId != null ? `p:${e.patientId}` : null;
    for (const run of buildRuns(rest, patientKey, opts.patientGapMin)) {
      if (run.events.length < opts.minMembers) continue;
      const label = patientLastName(run.events[0]) ?? "Patient";
      columnBars.push({
        id: `pat:${resource.id}:${run.events[0].startMin}`,
        resourceId: resource.id,
        kind: "patient",
        label,
        title: `${label} (${run.events.length} Termine hintereinander)`,
        color: uniformColor(run.events),
        startMin: run.events[0].startMin,
        endMin: run.endMin,
        eventIds: run.events.map((e) => e.id),
        memberCount: run.events.length,
        lane: 0,
      });
    }

    // Lane time-overlapping bars so they sit side by side, not on top of
    // each other (e.g. a patient run inside a bridged address-run gap).
    columnBars.sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);
    const active: { endMin: number; lane: number }[] = [];
    for (const bar of columnBars) {
      for (let i = active.length - 1; i >= 0; i--) {
        if (active[i].endMin <= bar.startMin) active.splice(i, 1);
      }
      const used = new Set(active.map((a) => a.lane));
      let lane = 0;
      while (used.has(lane)) lane++;
      bar.lane = lane;
      active.push({ endMin: bar.endMin, lane });
    }
    bars.push(...columnBars);
  }

  return bars;
};
